import type { ComponentType, ReactElement } from 'react';
import type {
  AuthFlow,
  AuthMfaFlow,
  AuthOtpChannel,
  AuthOtpRequestTrigger,
} from '@shared/utils/analytics/events';
import { MFA_CHALLENGE_INTERACTION_IDS } from './interactions';
import { isMfaChallengeScreen, MfaChallengeScreenNames } from './screenMapping';
import TrustDeviceScreen from './screens/TrustDeviceScreen';
import type { MfaScreenLayoutProps } from '../mfa/layout';
import MfaMethodSelectionScreen from '../mfa/MfaMethodSelectionScreen';
import TotpVerificationScreen from '../mfa/TotpVerificationScreen';
import bannerLoginImage from '../universal-login/images/banner-login-desktop.webp';

const MFA_CHALLENGE_LAYOUT_PROPS = {
  variant: 'banner',
  bannerImageDesktop: bannerLoginImage,
  hideLogo: false,
  headerClasses: 'lg:min-h-[76px]',
} as const;

type DescopeNext = (
  interactionId: string,
  payload?: Record<string, unknown>
) => Promise<unknown>;

type DescopeRecord = Record<string, unknown>;

type DescopeFlowState = DescopeRecord & {
  screenName?: string;
  error?: { text?: string };
  next?: DescopeNext;
};

export interface MfaChallengeOtpScreenProps extends MfaScreenLayoutProps {
  authFlow: AuthFlow;
  otpChannel?: AuthOtpChannel;
  mfaFlow?: AuthMfaFlow;
  consumeInitialOtpChallengeRequestTrigger?: () =>
    AuthOtpRequestTrigger | undefined;
  onFormUpdate: (data: DescopeRecord) => void;
  onOTPVerificationClick: (code: string) => Promise<void>;
  onClickResend: () => Promise<void>;
  errorText?: string;
  rawErrorText?: string;
  context: DescopeRecord;
  resetError: () => void;
}

export interface MfaChallengeRouteScreenProps {
  screenName?: string;
  state: DescopeFlowState;
  form: DescopeRecord;
  onFormUpdate: (data: DescopeRecord) => void;
  resetError: () => void;
  consumeInitialOtpChallengeRequestTrigger?: () =>
    AuthOtpRequestTrigger | undefined;
  OtpVerificationScreen: ComponentType<MfaChallengeOtpScreenProps>;
  errorText?: string;
}

export const routeMfaChallengeScreen = ({
  screenName,
  state,
  form,
  onFormUpdate,
  resetError,
  consumeInitialOtpChallengeRequestTrigger,
  OtpVerificationScreen,
  errorText,
}: MfaChallengeRouteScreenProps): ReactElement | null => {
  if (!screenName || !isMfaChallengeScreen(screenName)) {
    return null;
  }

  if (screenName === MfaChallengeScreenNames.SELECT_MFA) {
    return (
      <MfaMethodSelectionScreen
        {...MFA_CHALLENGE_LAYOUT_PROPS}
        mfaFlow="login_challenge"
        onClickSms={async () => {
          if (state.next) {
            await state.next(MFA_CHALLENGE_INTERACTION_IDS.SELECT_SMS, form);
          }
        }}
        onClickTotp={async () => {
          if (state.next) {
            await state.next(MFA_CHALLENGE_INTERACTION_IDS.SELECT_TOTP, form);
          }
        }}
        errorText={errorText ?? state?.error?.text}
      />
    );
  }

  if (screenName === MfaChallengeScreenNames.VERIFY_OTP) {
    return (
      <OtpVerificationScreen
        {...MFA_CHALLENGE_LAYOUT_PROPS}
        authFlow="login"
        otpChannel="sms"
        mfaFlow="login_challenge"
        consumeInitialOtpChallengeRequestTrigger={
          consumeInitialOtpChallengeRequestTrigger
        }
        onFormUpdate={onFormUpdate}
        onOTPVerificationClick={async (code: string) => {
          if (state.next) {
            await state.next(MFA_CHALLENGE_INTERACTION_IDS.VERIFY_OTP, {
              ...form,
              code,
            });
          }
        }}
        onClickResend={async () => {
          if (state.next) {
            await state.next(MFA_CHALLENGE_INTERACTION_IDS.RESEND_OTP, form);
          }
        }}
        errorText={errorText ?? state?.error?.text}
        rawErrorText={state?.error?.text}
        context={state}
        resetError={resetError}
      />
    );
  }

  if (screenName === MfaChallengeScreenNames.VERIFY_TOTP) {
    return (
      <TotpVerificationScreen
        {...MFA_CHALLENGE_LAYOUT_PROPS}
        authFlow="login"
        mfaFlow="login_challenge"
        onFormUpdate={onFormUpdate}
        onCodeSubmit={async (code: string) => {
          if (state.next) {
            await state.next(MFA_CHALLENGE_INTERACTION_IDS.VERIFY_TOTP, {
              ...form,
              code,
            });
          }
        }}
        errorText={errorText ?? state?.error?.text}
        rawErrorText={state?.error?.text}
        resetError={resetError}
      />
    );
  }

  return (
    <TrustDeviceScreen
      {...MFA_CHALLENGE_LAYOUT_PROPS}
      onClickNo={async () => {
        if (state.next) {
          await state.next(MFA_CHALLENGE_INTERACTION_IDS.TRUST_DEVICE_NO, form);
        }
      }}
      onClickYes={async () => {
        if (state.next) {
          await state.next(
            MFA_CHALLENGE_INTERACTION_IDS.TRUST_DEVICE_YES,
            form
          );
        }
      }}
      errorText={errorText ?? state?.error?.text}
      resetError={resetError}
    />
  );
};
